import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  inject
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ExemptVendor, Vendor, VendorTrade } from 'src/app/classes/models';
import { TradeCategory } from 'src/app/classes/models/trade-category';
import { VendorRanking } from 'src/app/classes/models/vendor-ranking';
import { GenericObject } from 'src/app/interfaces/generic-object';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { CompanyServiceRanking } from 'src/app/interfaces/models/company-service-ranking';
import { AuthService } from 'src/app/services/auth.service';
import { CompanyService } from 'src/app/services/company.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-preferred-vendor-ranking-selection-modal',
  templateUrl: './preferred-vendor-ranking-selection-modal.component.html',
  styleUrls: ['./preferred-vendor-ranking-selection-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PreferredVendorRankingSelectionModalComponent implements OnInit {
  private _cdr = inject(ChangeDetectorRef);

  constructor(
    public activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private CompanyService: CompanyService
  ) {}

  @Input() CompanyServiceRankings!: CompanyServiceRanking[];
  @Input() VendorRank: VendorRanking | null = null;
  @Input() Vendor?: Vendor | ExemptVendor;

  CompanyServiceRankingId: number | null = null;
  CompanyServiceRanking?: CompanyServiceRanking;
  selectedPrimaryKeyRows: Set<string | number> = new Set([]);
  tradeCategories: TradeCategory[] = [];
  trades: VendorTrade[] = [];

  close() {
    this.activeModal.dismiss('Cross Click');
  }

  closeModal(CompanyServiceRankingId: number | null) {
    this.activeModal.close(CompanyServiceRankingId);
  }

  selectRank() {
    if (!this.CompanyServiceRanking) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save rank. No CompanyServiceRanking found.',
        { returnError: true }
      );
    }

    if (!this.VendorRank) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save rank. No vendor rank found.',
        { returnError: true }
      );
    }

    const { isTradeCategorySpecific, isTradeSpecific } =
      this.CompanyServiceRanking;
    const { tradeCategoryName, tradeName } = this.VendorRank;

    if (
      (isTradeCategorySpecific && !tradeCategoryName) ||
      (isTradeSpecific && !tradeName)
    ) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot save rank. Missing required trade category or trade name.',
        { returnError: true }
      );
    }

    this.closeModal(this.CompanyServiceRankingId);
  }

  removeRank() {
    this.CompanyServiceRankingId = null;
    this.closeModal(this.CompanyServiceRankingId);
  }

  resetVendorRank() {
    if (this.VendorRank) {
      Object.assign(this.VendorRank, {
        tradeName: null,
        tradeCategoryName: null
      });
    }
  }

  setCompanyServiceRanking(id: number) {
    this.CompanyServiceRankingId = id;

    this.CompanyService.getCompanyServiceRanking({ id }).subscribe({
      next: (CompanyServiceRanking: CompanyServiceRanking) => {
        this.CompanyServiceRanking = CompanyServiceRanking;
        this._cdr.markForCheck();
      }
    });
  }

  actionEvent({
    action,
    rowData
  }: {
    action: GridActionColumnActions;
    rowData: any;
  }) {
    if (action === 'add') {
      const { id } = rowData as CompanyServiceRanking;

      this.resetVendorRank();
      this.setCompanyServiceRanking(id);
    }
  }

  ngOnInit(): void {
    if (this.Vendor) {
      const { VendorRankings, VendorTrades } = this.Vendor;

      if (VendorRankings && VendorRankings[0]) {
        this.VendorRank = VendorRankings[0];
        const { CompanyServiceRankingId, CompanyServiceRanking } =
          this.VendorRank;

        if (CompanyServiceRanking) {
          this.CompanyServiceRanking = CompanyServiceRanking;
        }

        if (CompanyServiceRankingId) {
          this.CompanyServiceRankingId = CompanyServiceRankingId;

          this.selectedPrimaryKeyRows.add(this.CompanyServiceRankingId);
        }
      }

      if (VendorTrades) {
        VendorTrades.forEach((trade) => {
          const { PrimaryTrade } = trade;

          if (PrimaryTrade) {
            const { name, TradeCategories } = PrimaryTrade;

            if (name) {
              this.trades.push(trade);
            }

            if (TradeCategories) {
              TradeCategories.forEach((category) => {
                const { categoryName } = category;

                if (categoryName) {
                  this.tradeCategories.push(category);
                }
              });
            }
          }
        });
      }

      this.tradeCategories = this.tradeCategories.filter(
        (category, index, self) => {
          return index === self.findIndex((c) => c.id === category.id);
        }
      );
    }
  }
}
